import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CheckboxFormControl, DateFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import { getInsurerOfferCategoryName, InsurerOfferCategoryValues, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import * as yup from 'yup'
import { useSelections } from '@/hooks'
import {
  useGetManualStornoContractsInfo,
  useManualStorno,
  type ManualStornoContractsInfo,
  type ManualStornoResponse,
} from '@/hooks/useWizard'
import { formatDate } from 'BrokerTsLib/src/Utils'

interface ManualStornoModalProps {
  insuranceId: number
  isOpen: boolean
  onClose: () => void
  onSuccess: (result: ManualStornoResponse) => void
  /** Skryje sekci "Vytvořit skladové pojištění" – pro použití na stránce vytvoření skladu. */
  hideCreateStock?: boolean
  /** Pokud je zadáno, zobrazí tlačítko "Pokračovat bez storna" místo Zrušit. */
  onSkip?: () => void
  /** Přednačtená data smluv – přeskočí interní fetch. */
  preloadedInfo?: ManualStornoContractsInfo
}

const schema = yup.object({
  cancellationTypeId: yup.string().required('Vyberte důvod storna.'),
  includeAsistId: yup.number().nullable().optional(),
  expectedEndDate: yup.date().optional(),
  requestedEndDate: yup.date().required('Zadejte požadované datum konce pojištění.'),
  createStock: yup.boolean().default(false),
  stockStartDate: yup.date().when('createStock', {
    is: true,
    then: s => s.required('Zadejte datum počátku skladového pojištění.'),
    otherwise: s => s.optional(),
  }).nullable(),
})

type ManualStornoFormValues = yup.InferType<typeof schema>

const today = new Date()
today.setHours(0, 0, 0, 0)

export const ManualStornoModal = ({
  insuranceId,
  isOpen,
  onClose,
  onSuccess,
  hideCreateStock = false,
  onSkip,
  preloadedInfo
}: ManualStornoModalProps) => {
  const { data: fetchedInfo, isLoading: isLoadingInfo } = useGetManualStornoContractsInfo(
    isOpen && !preloadedInfo ? insuranceId : undefined
  )
  const info = preloadedInfo ?? fetchedInfo
  const { mutateAsync: executeStorno, isPending } = useManualStorno()
  const { data: cancellationTypes } = useSelections(SelectionType.CancellationType)

  const methods = useForm<ManualStornoFormValues>({
    defaultValues: {
      cancellationTypeId: undefined,
      includeAsistId: null,
      expectedEndDate: undefined,
      requestedEndDate: undefined,
      createStock: false,
      stockStartDate: null,
    },
    resolver: yupResolver(schema),
  })

  const { handleSubmit, watch, setValue, reset, formState: { errors } } = methods

  const cancellationTypeId = watch('cancellationTypeId')
  const includeAsistId = watch('includeAsistId')
  const createStock = watch('createStock')
  const requestedEndDate = watch('requestedEndDate')

  const selectedType = cancellationTypes?.data.find(x => x.value === cancellationTypeId)
  const isStartCancellation = selectedType?.id == Selection.FromBeginning
  const isAgreement = selectedType?.id == Selection.Agreement
  const isDeregistrationOrTheft = selectedType?.id == Selection.Deregistration || selectedType?.id == Selection.Theft

  // requestedEndDate je viditelné pro Dohodu a Vyřazení/Odcizení, pro ostatní se nastavuje v pozadí
  const showRequestedEndDate = isAgreement || isDeregistrationOrTheft
  const minRequestedDate = isAgreement
    ? today
    : isDeregistrationOrTheft
      ? new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000)
      : undefined

  const handleCancellationTypeChange = (id: string | null) => {
    const type = cancellationTypes?.data.find(x => x.value === id)
    if (type?.id == Selection.FromBeginning && insuranceStartDate) {
      // Storno k počátku – obě data na insuranceStartDate, skryto
      setValue('expectedEndDate', insuranceStartDate, { shouldValidate: true })
      setValue('requestedEndDate', insuranceStartDate, { shouldValidate: true })
    }
  }

  // Uživatel mění requestedEndDate → expectedEndDate mirrors v pozadí
  const handleRequestedEndDateChange = (date: Date | undefined) => {
    setValue('expectedEndDate', date, { shouldValidate: true })
    if (createStock && date) {
      const next = new Date(date)
      next.setDate(next.getDate() + 1)
      setValue('stockStartDate', next)
    }
  }

  const handleCreateStockChange = (checked: boolean) => {
    if (checked) {
      const base = requestedEndDate ? new Date(requestedEndDate) : new Date(today)
      base.setDate(base.getDate() + 1)
      setValue('stockStartDate', base, { shouldValidate: true })
    } else {
      setValue('stockStartDate', null)
    }
  }

  const onSubmit = async (values: ManualStornoFormValues) => {
    const insuranceIds = [
      ...mandatoryContracts.map(c => c.insuranceId),
      ...(values.includeAsistId ? [values.includeAsistId] : []),
    ]

    // hodnota selectu je kód (string) – na server posíláme číselné id selekce
    const cancellationSelectionId = cancellationTypes?.data.find(x => x.value === values.cancellationTypeId)?.id

    const result = await executeStorno({
      insuranceId,
      insuranceIds,
      cancellationTypeId: cancellationSelectionId!,
      expectedEndDate: formatDate(values.expectedEndDate, 'yyyy-MM-dd'),
      requestedEndDate: formatDate(values.requestedEndDate, 'yyyy-MM-dd'),
      createStock: values.createStock,
      stockStartDate: values.createStock ? formatDate(values.stockStartDate ?? undefined, 'yyyy-MM-dd') : undefined,
    })

    if (result.success || result.cancelledContracts?.length > 0) {
      onSuccess(result)
      handleClose()
    } else {
      methods.setError('root', {
        message: result.errors?.length > 0 ? result.errors.join(' | ') : 'Storno selhalo.',
      })
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const contracts = info?.contracts ?? []
  const mandatoryContracts = contracts.filter(c => c.isMandatory)
  const asistContract = contracts.find(c => c.category === InsurerOfferCategoryValues.Assistance)

  const insuranceStartDate = info?.insuranceStartDate ? new Date(info.insuranceStartDate) : undefined
  const now = new Date()
  const isStartCancellationAllowed = insuranceStartDate
    ? insuranceStartDate > new Date(now.getTime() - 24 * 60 * 60 * 1000)
    : false

  const cancellationTypeOptions = (cancellationTypes?.data ?? [])
    .filter(t => {
      if (t.id == Selection.Agreement || t.id == Selection.Deregistration || t.id == Selection.Theft)
        return true

      if (t.id == Selection.FromBeginning)
        return isStartCancellationAllowed

      return false
    })
    .map(t => ({ value: t.value ?? '', label: t.description ?? '' }))

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Storno pojistné smlouvy</ModalHeader>
        <ModalCloseButton />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <ModalBody>
              <Stack gap={4}>
                {isLoadingInfo && <Text>Načítám informace o smlouvách…</Text>}

                {/* Seznam PS k ukončení */}
                {!isLoadingInfo && contracts.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>Pojistné smlouvy k ukončení</Text>
                    <VStack align="stretch" gap={2}>
                      {mandatoryContracts.sort((a, b) => a.category! - b.category!).map(c => (
                        <Checkbox key={c.insuranceId} isChecked isDisabled spacing={3}>
                          <Text as="span" fontWeight="medium">{c.contractNumber}</Text>
                          <Text as="span" color="gray.500" ml={2}>({getInsurerOfferCategoryName(c.category)})</Text>
                        </Checkbox>
                      ))}
                      {asistContract && !asistContract.isMandatory && (
                        <Checkbox
                          isChecked={!!includeAsistId}
                          onChange={e => setValue('includeAsistId', e.target.checked ? asistContract.insuranceId : null)}
                          spacing={3}
                        >
                          <Text as="span" fontWeight="medium">{asistContract.contractNumber}</Text>
                          <Text as="span" color="gray.500" ml={2}>({getInsurerOfferCategoryName(InsurerOfferCategoryValues.Assistance)})</Text>
                        </Checkbox>
                      )}
                    </VStack>
                  </Box>
                )}

                <Divider />

                {/* Důvod storna */}
                <SelectFormControl
                  name="cancellationTypeId"
                  control={methods.control}
                  label="Důvod storna"
                  unselectedText="Vyberte důvod"
                  options={cancellationTypeOptions}
                  onChange={(val) => handleCancellationTypeChange(val as string | null)}
                  w="full"
                />

                {/* Požadované datum konce pojištění – viditelné pro Dohodu a Vyřazení/Odcizení */}
                {showRequestedEndDate && (
                  <DateFormControl
                    name="requestedEndDate"
                    control={methods.control}
                    label="Požadované datum konce pojištění"
                    isRequired
                    minDate={minRequestedDate}
                    onChange={(d) => handleRequestedEndDateChange((d as Date | null) ?? undefined)}
                    helperText={isDeregistrationOrTheft ? 'Lze vybrat zpětně až 6 měsíců (180 dní).' : undefined}
                    w="full"
                  />
                )}
                {/* expectedEndDate – vždy skryté, mirrors requestedEndDate */}

                {/* Skladové pojištění – jen pro auto-pojištění, skryté při zakládání skladu */}
                {!hideCreateStock && info?.canCreateStock && (
                  <>
                    <Divider />
                    <CheckboxFormControl
                      name="createStock"
                      control={methods.control}
                      label="Vytvořit skladové pojištění"
                      onChange={(val) => handleCreateStockChange(val as boolean)}
                    />

                    {createStock && (
                      <DateFormControl
                        name="stockStartDate"
                        control={methods.control}
                        label="Datum počátku skladového pojištění"
                        isRequired
                        w="full"
                      />
                    )}
                  </>
                )}

                {/* Globální chyby */}
                {errors.root && (
                  <Alert status="error">
                    <AlertIcon />
                    <Text>{errors.root.message}</Text>
                  </Alert>
                )}
              </Stack>
            </ModalBody>

            <ModalFooter gap={3}>
              <Button variant="ghost" onClick={handleClose} isDisabled={isPending} mr='auto'>
                Zrušit
              </Button>

              {onSkip &&
                <Button variant="outline" onClick={onSkip} isDisabled={isPending}>
                  Pokračovat bez storna
                </Button>
              }

              <Button
                type="submit"
                colorScheme="red"
                isLoading={isPending}
                isDisabled={isLoadingInfo || contracts.length === 0}
              >
                Potvrdit storno
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}
